<template>
  <div>
    <h5>Request Approval</h5>
    <h6 class="text-uppercase">Estimate ID: {{ serviceOrderId }}</h6>
    <span class="required-legend float-right p-0">* Required</span>

    <div v-if="allowNonVCPEstimateLinks && !isVCPCustomer">
      <b-row>
        <b-form-group
          class="customer-conacts"
          label="Customer Contacts:"
          label-class="col-head"
          label-for="customerContacts"
        >
          <v-select
            id="customerContacts"
            v-model="selected"
            :options="customerContacts"
            :clearable="false"
            :get-option-label="item => item.email"
            select-on-tab
            @input="selectContact($event)"
          >
            <template #option="contact">
              <span>{{ contact.firstName }} {{ contact.lastName }} | {{ contact.emailAddress }}</span>
            </template>
            <template #selected-option="contact">
              <span>{{ contact.firstName }} {{ contact.lastName }} | {{ contact.emailAddress }}</span>
            </template>
          </v-select>
        </b-form-group>
      </b-row>
      <b-row no-gutters>
        <b-col>
          <b-form-group label="First Name:" label-class="col-head" label-for="firstName">
            <div v-if="readonly" class="col-form-label">
              {{ firstName || NA }}
            </div>
            <b-input-group v-else>
              <b-form-input
                v-model="firstName"
                :state="!$v.firstName.$error ? null : false"
                class="rounded"
              ></b-form-input>
              <span class="required-asterisk">*</span>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col class="ml-2">
          <b-form-group label="Last Name:" label-class="col-head" label-for="lastName">
            <div v-if="readonly" class="col-form-label">
              {{ lastName || NA }}
            </div>
            <b-input-group v-else>
              <b-form-input
                v-model="lastName"
                :state="!$v.lastName.$error ? null : false"
                class="rounded"
              ></b-form-input>
              <span class="required-asterisk">*</span>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row no-gutters>
        <b-col>
          <b-form-group label="Email:" label-class="col-head" label-for="email">
            <div v-if="readonly" class="col-form-label">
              {{ emailAddress || NA }}
            </div>
            <b-input-group v-else>
              <b-form-input v-model="email" :state="!$v.email.$error ? null : false" class="rounded"></b-form-input>
              <span class="required-asterisk">*</span>
            </b-input-group>
            <div v-show="$v.email.$error && !$v.email.email" class="error">Enter a valid Email Address.</div>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <b-row no-gutters>
        <b-col sm="12" lg="6">
          <b-form-group label="Approver:" label-class="col-head" label-for="approver">
            <b-input-group>
              <employee-select
                id="approver"
                v-model="approver"
                :options="approverList"
                :placeholder="approverPlaceholder"
                :class="$v.approver.$error && !$v.approver.required ? 'is-invalid' : ''"
                no-options-text="No approvers available."
                :search-in-options="true"
              />
              <span class="required-asterisk">*</span>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row no-gutters>
        <b-col>
          <b-form-group label="Notify:" label-class="col-head" class="required-placeholder">
            <b-input-group>
              <employee-select v-model="recipients" multiple :options="recipientList" :allow-customer-search="true" />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <b-row no-gutters>
      <b-col>
        <b-form-group label="Comments:" label-class="col-head" label-for="comments" class="required-placeholder">
          <b-textarea id="comments" v-model="comments" class="overflow-auto" size="sm" rows="3" max-rows="4" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row no-gutters>
      <b-col md="12" lg="6" xl="5">
        <date-time-input
          id="asideDatePromise"
          v-model="$v.datePromise.$model"
          label="Promised (ETR):"
          label-class="col-head"
          :validator="$v.datePromise"
          :readonly="readonly"
          required
          :disabled-dates="disabledDates"
        />
      </b-col>
    </b-row>

    <b-button
      variant="primary"
      :disabled="sending || !datePromise || !canSendRequest || hasServiceOrderChanges"
      @click="send"
    >
      <div v-show="sending">
        <b-spinner small></b-spinner>
        Sending...
      </div>
      <div v-show="!sending">Send</div>
    </b-button>
    <div v-if="!canSendRequest || hasServiceOrderChanges" class="note">
      {{ warningMessage }}
    </div>
  </div>
</template>

<script>
// Components
import EmployeeSelectComponent from '@/shared/components/ui/EmployeeSelectComponent';
import timeInput from '@/shared/directives/TimeInput';
import DateTimeInput from '@/shared/components/ui/DateTimeInput';
import vSelect from 'vue-select';

// Vuex
import { CustomerGetters } from '@/shared/store/customer/types';
import { ServiceOrderActions, ServiceOrderGetters, ServiceOrderMutations } from '@/shared/store/service-order/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
// Helpers
import { required, requiredIf } from 'vuelidate/lib/validators';
import ErrorService from '@/shared/services/ErrorService';
import SuccessService from '@/shared/services/SuccessService';
import { JOB_OPERATION_STATUS_IDS } from '@/shared/store/lookup/state';

export default {
  name: 'ServiceOrderRequestApproval',
  components: {
    'employee-select': EmployeeSelectComponent,
    'date-time-input': DateTimeInput,
    'v-select': vSelect
  },
  directives: {
    timeInput
  },
  data() {
    return {
      sending: false,
      approverPlaceholder: null,
      selected: null,
      hasServiceOrderChanges: false,
      disabledDates: {
        to: new Date(Date.now() - 86400000) // Yesterday and all dates before
      }
    };
  },
  validations: {
    approver: {
      required: requiredIf(function () {
        return this.isVCPCustomer;
      })
    },
    datePromise: {
      // minValue: minValue(minimumDate()),
      required
    },
    firstName: {
      required: requiredIf(function () {
        return !this.isVCPCustomer;
      })
    },
    lastName: {
      required: requiredIf(function () {
        return !this.isVCPCustomer;
      })
    },
    email: {
      required: requiredIf(function () {
        return !this.isVCPCustomer;
      })
    }
  },
  computed: {
    ...mapState('unit', ['unit']),
    ...mapGetters({
      serviceOrder: ServiceOrderGetters.GET_SERVICE_ORDER,
      branchId: ServiceOrderGetters.GET_BRANCH_ID,
      readonly: ServiceOrderGetters.GET_READONLY,
      requestApproval: ServiceOrderGetters.GET_REQUEST_APPROVAL,
      approverList: CustomerGetters.GET_APPROVER_LIST,
      jobs: ServiceOrderGetters.GET_JOBS,
      vcpUserList: CustomerGetters.GET_VCP_USER_LIST,
      employeeList: LookupGetters.GET_EMPLOYEE_LIST,
      repairContact: ServiceOrderGetters.GET_REPAIR_CONTACT,
      hasChanges: ServiceOrderGetters.HAS_CHANGES
    }),
    allowNonVCPEstimateLinks() {
      return this.serviceOrder.allowNonVCPEstimateLinks;
    },
    serviceOrderId() {
      return this.serviceOrder.serviceOrderId;
    },
    timestampSVSLS() {
      return this.serviceOrder.timestampSVSLS;
    },
    canSendRequest() {
      return (
        this.jobs &&
        Object.values(this.jobs).some(job => job.jobOperationStatusId === JOB_OPERATION_STATUS_IDS.ESTIMATE)
      );
    },
    warningMessage() {
      let warningString = '';
      if (!this.canSendRequest) warningString += 'One or more jobs in Estimate status required to send request. ';
      if (this.hasServiceOrderChanges)
        warningString += 'Estimate has unsaved changes and must be saved before requesting approval.';
      return warningString;
    },
    recipientList() {
      return [...this.employeeList, ...this.vcpUserList];
    },
    isVCPCustomer() {
      return this.serviceOrder.customer.isVCPCustomer;
    },
    approver: {
      get() {
        return this.requestApproval.approver;
      },
      set(value) {
        this.setData('approver', value);
      }
    },
    recipients: {
      get() {
        return this.requestApproval.recipients;
      },
      set(value) {
        this.setData('recipients', value);
      }
    },
    comments: {
      get() {
        return this.requestApproval.comments;
      },
      set(value) {
        this.setData('comments', value);
      }
    },
    datePromise: {
      get() {
        return this.requestApproval.datePromise;
      },
      set(value) {
        if (value == this.datePromise) return;
        this.setData('datePromise', value);
      }
    },
    firstName: {
      get() {
        return this.requestApproval.firstName;
      },
      set(value) {
        this.setData('firstName', value);
      }
    },
    lastName: {
      get() {
        return this.requestApproval.lastName;
      },
      set(value) {
        this.setData('lastName', value);
      }
    },
    email: {
      get() {
        return this.requestApproval.email;
      },
      set(value) {
        this.setData('email', value);
      }
    },
    customerContacts() {
      return this.repairContact?.customerContacts?.filter(pc => pc.emailAddress);
    }
  },
  created() {
    this.hasServiceOrderChanges = this.hasChanges();
  },
  methods: {
    ...mapActions([ServiceOrderActions.SEND_APPROVAL_REQUEST, ServiceOrderActions.SEND_LINK_FOR_ESTIMATE]),
    ...mapMutations([ServiceOrderMutations.SET_PROP, ServiceOrderMutations.SET_APPROVER_INFO]),
    selectContact(item) {
      this.setData('firstName', item.firstName);
      this.setData('lastName', item.lastName);
      this.setData('title', item.title);
      this.setData('email', item.emailAddress);
    },
    async send() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        this.approverPlaceholder = 'Select Approver';
        ErrorService.createErrorToast(this, 'Error requesting approval. See indicated fields below.');
        return;
      }
      try {
        this.sending = true;
        if (!this.isVCPCustomer) {
          this[ServiceOrderMutations.SET_APPROVER_INFO](this.requestApproval);
        }
        await this[ServiceOrderActions.SEND_APPROVAL_REQUEST]({
          approver: this.approver,
          recipients: this.recipients,
          comments: this.comments,
          datePromise: this.datePromise,
          timestampSVSLS: this.timestampSVSLS,
          isVCPCustomer: this.isVCPCustomer
        });

        SuccessService.createSuccessToast(this.$root, `Request for Approval email successfully sent.`);
        this.resetForm();
      } catch (error) {
        ErrorService.createErrorToast(this, 'Failed to send approval request.');
      } finally {
        this.sending = false;
      }
    },
    resetForm() {
      this.approverPlaceholder = null;
      this.selected = null;
      this.$v.$reset();
    },
    setData(key, newValue) {
      let value = { ...this.requestApproval };
      value[key] = newValue;
      this[ServiceOrderMutations.SET_PROP]({ key: 'requestApproval', value });
    },
    setApproverData(key, newValue) {
      let value = { ...this.requestApproval.approver };
      value[key] = newValue;
      this[ServiceOrderMutations.SET_PROP]({ key: 'approver', value });
    }
  }
};
</script>
